/*
 * SR Native Cart — replaces Rebuy Smart Cart (Sackett Ranch, Oct 2026)
 * Works with sections/sr-cart-drawer.liquid + assets/sr-cart.css.
 * Uses only Shopify storefront APIs: Cart AJAX (/cart/*.js), Section Rendering, Product Recommendations.
 */
(() => {
  if (window.SRCart) return;

  const SECTION = 'sr-cart-drawer';
  const root = () => (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const url = (p) => root().replace(/\/$/, '') + p;
  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const APPAREL = /shirt|tee|hoodie|sweat|hat|cap|jacket|apparel|outerwear/i;

  /* ---------------- money ---------------- */
  function formatMoney(cents, format) {
    const fmt = format || '${{amount}}';
    const value = (Number(cents) || 0) / 100;
    const withDelims = (n, dec, thou, decimals) => {
      const parts = n.toFixed(decimals).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thou);
      return parts.join(dec);
    };
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return withDelims(value, '.', ',', 0);
        case 'amount_with_comma_separator': return withDelims(value, ',', '.', 2);
        case 'amount_no_decimals_with_comma_separator': return withDelims(value, ',', '.', 0);
        default: return withDelims(value, '.', ',', 2);
      }
    });
  }

  /* ---------------- product data: one shape for recommendation JSON and the add-on pool ---------------- */
  const sized = (src, w) => {
    if (!src) return '';
    if (typeof src === 'object') src = src.src || src.url || '';
    if (!src) return '';
    if (src.startsWith('//')) src = 'https:' + src;
    return /[?&]width=/.test(src) ? src : src + (src.includes('?') ? '&' : '?') + 'width=' + w;
  };
  function normalize(p) {
    if (!p || p._sr) return p;
    const options = (p.options || []).map((o, i) => (typeof o === 'string'
      ? { name: o, values: [...new Set((p.variants || []).map((v) => v['option' + (i + 1)]).filter(Boolean))] }
      : { name: o.name, values: o.values || [] }));
    const variants = (p.variants || []).map((v) => ({
      id: v.id,
      title: v.title,
      price: v.price,
      compare: v.compare_at_price || 0,
      available: !!v.available,
      opts: [v.option1, v.option2, v.option3].filter((x) => x != null),
      image: sized(v.featured_image, 500)
    }));
    return {
      _sr: true,
      id: p.id,
      title: p.title,
      handle: p.handle,
      url: p.url,
      type: p.type || '',
      gift_card: !!p.gift_card,
      available: !!p.available,
      image: sized(p.featured_image || (p.images && p.images[0]), 500),
      compare: p.compare_at_price || 0,
      options: options.filter((o) => !(o.name === 'Title' && o.values.length <= 1)),
      variants
    };
  }

  /* ---------------- request queue (cart writes must be sequential) ---------------- */
  let queue = Promise.resolve();
  const enqueue = (fn) => (queue = queue.then(fn, fn));

  async function postJSON(path, body) {
    const res = await fetch(url(path), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
      credentials: 'same-origin'
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const err = new Error(data.description || data.message || res.statusText);
      err.status = res.status;
      throw err;
    }
    return data;
  }

  /* ---------------- theme bridge (Blockshop) ---------------- */
  function syncThemeCounters(cart) {
    try {
      const C = customElements.get('cart-element');
      if (C && cart && cart.items) {
        C.fillBasket(cart.items);
        C.updateTotals(cart.item_count);
      }
    } catch (e) { /* theme without Blockshop cart */ }
    document.querySelectorAll('[data-cart-count]').forEach((el) => el.setAttribute('data-cart-count', cart.item_count));
    document.querySelectorAll('.cart-count, [data-sr-cart-count]').forEach((el) => { el.textContent = cart.item_count; });
  }

  /* ======================================================================== */
  class SRCartElement extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('.sr-cart__panel');
      this.moneyFormat = this.dataset.moneyFormat;
      this.recCache = new Map();
      this.products = new Map();
      this.lastAddedProductId = null;
      this.isOpen = false;
      this.bind();
      this.bindRecs();
      this.renderRecs();
    }

    /* ---------- helpers ---------- */
    state() {
      const el = this.querySelector('[data-sr-state]');
      try { return JSON.parse(el.textContent); } catch (e) { return { itemCount: 0, productIds: [], types: [], gifts: [], giftLines: [] }; }
    }
    addons() {
      if (this._addons) return this._addons;
      const el = this.querySelector('[data-sr-addons]');
      try { this._addons = el ? JSON.parse(el.textContent).map(normalize) : []; } catch (e) { this._addons = []; }
      return this._addons;
    }
    money(c) { return formatMoney(c, this.moneyFormat); }
    announce(msg) { const el = this.querySelector('[data-sr-live]'); if (el) { el.textContent = ''; setTimeout(() => (el.textContent = msg), 50); } }
    loading(on) { const el = this.querySelector('[data-sr-loading]'); if (el) el.hidden = !on; if (this.panel) this.panel.setAttribute('aria-busy', on ? 'true' : 'false'); }

    /* ---------- open / close ---------- */
    open(trigger) {
      if (this.isOpen) { this.refresh(); return; }
      this.isOpen = true;
      this.opener = trigger || document.activeElement;
      this.hidden = false;
      this.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('sr-cart-locked');
      const show = () => { this.classList.add('is-open'); this.panel.focus({ preventScroll: true }); };
      requestAnimationFrame(show);
      setTimeout(show, 60); // rAF is paused in background tabs
      this.refresh();
      document.dispatchEvent(new CustomEvent('sr-cart:open'));
    }
    close() {
      if (!this.isOpen) return;
      this.closeSheet();
      this.isOpen = false;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('sr-cart-locked');
      const done = () => { if (!this.isOpen) this.hidden = true; };
      this.panel.addEventListener('transitionend', done, { once: true });
      setTimeout(done, 400);
      if (this.opener && typeof this.opener.focus === 'function' && document.contains(this.opener)) this.opener.focus({ preventScroll: true });
      document.dispatchEvent(new CustomEvent('sr-cart:close'));
    }

    /* ---------- render ---------- */
    async refresh() {
      try {
        // Render against /cart: never edge-cached, and present in every theme layout.
        const res = await fetch(`${url('/cart')}?sections=${SECTION}`, { credentials: 'same-origin' });
        const data = await res.json();
        if (data[SECTION]) this.applySection(data[SECTION]);
        const cart = await fetch(url('/cart.js'), { credentials: 'same-origin' }).then((r) => r.json());
        syncThemeCounters(cart);
        await this.reconcileGifts();
      } catch (e) { console.warn('[SR Cart] refresh failed', e); }
    }

    applySection(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      doc.querySelectorAll('[data-sr-region]').forEach((fresh) => {
        const name = fresh.getAttribute('data-sr-region');
        const current = this.querySelector(`[data-sr-region="${name}"]`);
        if (current) current.innerHTML = fresh.innerHTML;
      });
      const st = this.state();
      const foot = this.querySelector('[data-sr-foot]');
      if (foot) foot.hidden = st.itemCount === 0;
      const bottom = this.querySelector('[data-sr-bottom]');
      if (bottom) bottom.hidden = st.itemCount === 0;
      this.renderRecs();
    }

    async cartWrite(path, body, { quiet } = {}) {
      return enqueue(async () => {
        this.loading(true);
        try {
          const data = await postJSON(path, { ...body, sections: SECTION, sections_url: url('/cart') });
          if (data.sections && data.sections[SECTION]) this.applySection(data.sections[SECTION]);
          else setTimeout(() => this.refresh(), 0);
          const cart = data.items && data.item_count !== undefined ? data : await fetch(url('/cart.js')).then((r) => r.json());
          syncThemeCounters(cart);
          document.dispatchEvent(new CustomEvent('sr-cart:updated', { detail: { cart } }));
          return data;
        } catch (e) {
          if (!quiet) this.announce(e.message || 'Something went wrong. Please try again.');
          this.flashError(e.message);
          throw e;
        } finally {
          this.loading(false);
        }
      }).then(async (d) => { await this.reconcileGifts(); return d; });
    }

    flashError(msg) {
      if (!msg) return;
      const body = this.querySelector('.sr-cart__body');
      if (!body) return;
      let el = this.querySelector('.sr-error');
      if (!el) {
        el = document.createElement('p');
        el.className = 'sr-error';
        el.setAttribute('role', 'alert');
        body.prepend(el);
      }
      el.textContent = msg;
      clearTimeout(this._errT);
      this._errT = setTimeout(() => el.remove(), 5000);
    }

    /* ---------- line edits ---------- */
    changeLine(key, quantity) {
      return this.cartWrite('/cart/change.js', { id: key, quantity: Math.max(0, quantity) });
    }

    /* ---------- free gift tiers ---------- */
    async reconcileGifts() {
      if (this._reconciling) return;
      const st = this.state();
      if (!st.gifts || (!st.gifts.length && !(st.giftLines || []).length)) return;
      this._reconciling = true;
      try {
        for (const line of st.giftLines || []) {
          const tier = (st.gifts || []).find((g) => g.tier === line.tier);
          if (!tier || st.eligibleCents < tier.thresholdCents) {
            await this.cartWrite('/cart/change.js', { id: line.key, quantity: 0 }, { quiet: true });
            return;
          }
        }
        for (const g of st.gifts || []) {
          if (!g.available || !g.variantId) continue;
          const has = (st.giftLines || []).some((l) => l.tier === g.tier);
          if (st.eligibleCents >= g.thresholdCents && !has && st.itemCount > 0) {
            await this.cartWrite('/cart/add.js', { items: [{ id: g.variantId, quantity: 1, properties: { _sr_gift: g.tier } }] }, { quiet: true });
            this.announce('Free gift added to your cart');
            return;
          }
        }
      } catch (e) { /* gift sold out etc. — never block the cart */ }
      finally { this._reconciling = false; }
    }

    /* ---------- discount codes ---------- */
    async applyCode(code) {
      const msg = this.querySelector('[data-sr-code-msg]');
      code = (code || '').trim();
      if (!code) return;
      try {
        const cart = await fetch(url('/cart.js')).then((r) => r.json());
        const existing = (cart.discount_codes || []).map((d) => d.code).filter((c) => c.toUpperCase() !== code.toUpperCase());
        const data = await this.cartWrite('/cart/update.js', { discount: [...existing, code].join(',') });
        const applied = (data.discount_codes || []).find((d) => d.code.toUpperCase() === code.toUpperCase());
        if (msg) msg.textContent = applied && applied.applicable ? `Code ${code.toUpperCase()} applied.` : `Code ${code.toUpperCase()} isn't valid for the items in your cart.`;
        if (applied && !applied.applicable) await this.cartWrite('/cart/update.js', { discount: existing.join(',') }, { quiet: true });
      } catch (e) { if (msg) msg.textContent = 'Could not apply that code.'; }
    }
    async removeCode(code) {
      const cart = await fetch(url('/cart.js')).then((r) => r.json());
      const keep = (cart.discount_codes || []).map((d) => d.code).filter((c) => c.toUpperCase() !== String(code).toUpperCase());
      await this.cartWrite('/cart/update.js', { discount: keep.join(',') });
    }

    /* ---------- events ---------- */
    bind() {
      this.addEventListener('click', (e) => {
        const t = e.target;
        if (t.closest('[data-sr-close]')) { e.preventDefault(); this.close(); return; }

        const step = t.closest('[data-sr-qty-step]');
        if (step) {
          const li = step.closest('[data-key]');
          const input = li.querySelector('[data-sr-qty-input]');
          const q = (parseInt(input.value, 10) || 0) + parseInt(step.dataset.srQtyStep, 10);
          input.value = Math.max(0, q);
          clearTimeout(li._t);
          li._t = setTimeout(() => this.changeLine(li.dataset.key, Math.max(0, q)), 250);
          return;
        }
        const rm = t.closest('[data-sr-remove]');
        if (rm) {
          const li = rm.closest('[data-key]');
          li.classList.add('is-removing');
          this.changeLine(li.dataset.key, 0).then(() => this.announce('Item removed'));
          return;
        }
        if (t.closest('[data-sr-apply-code]')) { const i = this.querySelector('[data-sr-code-input]'); this.applyCode(i && i.value); return; }
        const rc = t.closest('[data-sr-remove-code]');
        if (rc) { this.removeCode(rc.dataset.srRemoveCode); return; }
      });

      this.addEventListener('change', (e) => {
        const input = e.target.closest('[data-sr-qty-input]');
        if (input) {
          const li = input.closest('[data-key]');
          this.changeLine(li.dataset.key, parseInt(input.value, 10) || 0);
        }
        const note = e.target.closest('[data-sr-note]');
        if (note) enqueue(() => postJSON('/cart/update.js', { note: note.value }).catch(() => {}));
      });

      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') { e.preventDefault(); if (this._sheet && !this._sheet.hidden) this.closeSheet(); else this.close(); return; }
        if (e.key === 'Enter' && e.target.matches('[data-sr-code-input]')) { e.preventDefault(); this.applyCode(e.target.value); return; }
        if (e.key === 'Tab') this.trapFocus(e);
      });

      this.addEventListener('submit', () => {
        const btn = this.querySelector('[data-sr-checkout]');
        if (btn) { btn.setAttribute('aria-busy', 'true'); btn.classList.add('is-busy'); }
      });
      window.addEventListener('pageshow', (e) => {
        if (e.persisted) { const btn = this.querySelector('[data-sr-checkout]'); if (btn) { btn.removeAttribute('aria-busy'); btn.classList.remove('is-busy'); } }
      });
    }

    trapFocus(e) {
      const scope = this._sheet && !this._sheet.hidden ? this._sheet : this.panel;
      const f = [...scope.querySelectorAll('a[href], button:not([disabled]), input:not([type="hidden"]), textarea, select, summary, [tabindex]:not([tabindex="-1"])')]
        .filter((el) => el.offsetParent !== null && !el.closest('[hidden]'));
      if (!f.length) return;
      const first = f[0];
      const last = f[f.length - 1];
      if (e.shiftKey && (document.activeElement === first || document.activeElement === scope)) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }

    /* ======================= RECOMMENDATIONS ======================= */
    bindRecs() {
      this.addEventListener('click', (e) => {
        const t = e.target;
        const quick = t.closest('[data-sr-rec-add]');
        if (quick) { e.preventDefault(); this.quickAdd(quick); return; }
        const opener = t.closest('[data-sr-rec-open]');
        if (opener) { e.preventDefault(); this.openSheet(opener.closest('[data-product-id]').dataset.productId, opener); return; }
        if (t.closest('[data-sr-sheet-close]')) { e.preventDefault(); this.closeSheet(); return; }
        const chip = t.closest('[data-sr-chip]');
        if (chip && !chip.disabled) { e.preventDefault(); this.pickChip(chip); return; }
        if (t.closest('[data-sr-sheet-add]')) { e.preventDefault(); this.sheetAdd(); return; }
        const arrow = t.closest('.sr-recs__arrow');
        if (arrow) {
          const list = this.querySelector('[data-sr-recs-list]');
          list.scrollBy({ left: (arrow.dataset.dir === 'prev' ? -1 : 1) * list.clientWidth * 0.8, behavior: 'smooth' });
        }
      });
    }

    async fetchRecs(productId, intent) {
      const key = `${productId}:${intent}`;
      if (this.recCache.has(key)) return this.recCache.get(key);
      const p = fetch(`${url('/recommendations/products.json')}?product_id=${productId}&limit=8&intent=${intent}`, { credentials: 'same-origin' })
        .then((r) => (r.ok ? r.json() : { products: [] }))
        .then((d) => (d.products || []).map(normalize))
        .catch(() => []);
      this.recCache.set(key, p);
      return p;
    }

    recHeading(st) {
      const types = (st.types || []).filter(Boolean);
      const apparel = types.some((t) => APPAREL.test(t));
      return apparel ? (this.dataset.recTitleApparel || 'Complete the outfit') : (this.dataset.recTitleOther || 'Complete the look');
    }

    async renderRecs() {
      const wrap = this.querySelector('[data-sr-recs]');
      const list = this.querySelector('[data-sr-recs-list]');
      if (!wrap || !list) return;
      const token = (this._recToken = (this._recToken || 0) + 1);
      const st = this.state();
      const limit = parseInt(this.dataset.recLimit, 10) || 4;
      const exclude = new Set((this.dataset.recExclude || '').split(/[\s,]+/).filter(Boolean));
      const inCart = new Set((st.productIds || []).map(String));
      const cartTypes = new Set((st.types || []).filter(Boolean).map((t) => t.toLowerCase()));
      const remaining = st.nextThresholdCents ? st.nextThresholdCents - st.eligibleCents : 0;
      const gapCloser = this.dataset.recGapCloser === 'true' && remaining > 0;

      if (!st.itemCount) { wrap.hidden = true; return; }
      const title = wrap.querySelector('.sr-recs__title');
      if (title) title.textContent = this.recHeading(st);

      // Seed: most recently added product first (Shopify returns newest line first), then up to 2 more.
      const seeds = [];
      if (this.lastAddedProductId && inCart.has(String(this.lastAddedProductId))) seeds.push(String(this.lastAddedProductId));
      (st.productIds || []).forEach((id) => { id = String(id); if (!seeds.includes(id) && seeds.length < 3) seeds.push(id); });

      const sources = [];
      if (seeds[0]) sources.push(this.fetchRecs(seeds[0], 'complementary').then((ps) => ps.map((p) => ({ p, src: 'complementary' }))));
      seeds.forEach((id) => sources.push(this.fetchRecs(id, 'related').then((ps) => ps.map((p) => ({ p, src: 'related' })))));
      const results = (await Promise.all(sources)).flat();
      this.addons().forEach((p) => results.push({ p, src: 'addon' }));
      if (token !== this._recToken) return; // a newer render started

      const pool = new Map();
      for (const { p, src } of results) {
        if (!p || !p.available || inCart.has(String(p.id)) || exclude.has(p.handle) || p.gift_card) continue;
        const live = p.variants.filter((v) => v.available);
        if (!live.length) continue;
        const minPrice = Math.min(...live.map((v) => v.price));
        const prev = pool.get(p.id);
        let score = prev ? prev.score : 0;
        score += src === 'complementary' ? 40 : src === 'related' ? 12 : 18;
        const type = (p.type || '').toLowerCase();
        let unlocks = prev ? prev.unlocks : false;
        if (!prev) {
          if (type && !cartTypes.has(type)) score += 22; // complete the look, don't sell a 3rd tee
          if (gapCloser && minPrice >= remaining) {
            unlocks = true;
            score += Math.max(8, 35 - (minPrice - remaining) / 100); // closest-to-gap first
          }
        }
        pool.set(p.id, { p, live, minPrice, score, unlocks, type });
      }

      const ranked = [...pool.values()].sort((a, b) => b.score - a.score);
      const picked = [];
      const perType = {};
      for (const r of ranked) {
        const k = r.type || 'other';
        if ((perType[k] || 0) >= 2) continue;
        perType[k] = (perType[k] || 0) + 1;
        picked.push(r);
        if (picked.length >= limit) break;
      }

      if (!picked.length) { wrap.hidden = true; return; }
      picked.forEach((r) => this.products.set(String(r.p.id), r));
      list.innerHTML = picked.map((r) => this.recCard(r)).join('');
      wrap.hidden = false;
      if (this.dataset.recLayout === 'carousel' && picked.length > 2 && !wrap.querySelector('.sr-recs__arrows')) {
        title.insertAdjacentHTML('afterend',
          '<div class="sr-recs__arrows"><button type="button" class="sr-recs__arrow" data-dir="prev" aria-label="Previous recommendations">‹</button><button type="button" class="sr-recs__arrow" data-dir="next" aria-label="More recommendations">›</button></div>');
      }
    }

    recCard({ p, live, minPrice, unlocks }) {
      const single = p.variants.length === 1;
      const compare = p.compare && p.compare > minPrice ? `<s>${this.money(p.compare)}</s>` : '';
      const priceLabel = live.some((v) => v.price !== minPrice) ? `From ${this.money(minPrice)}` : this.money(minPrice);
      const sizeOpt = p.options.find((o) => /size/i.test(o.name));
      const label = single ? 'Add' : sizeOpt ? 'Choose size' : 'Choose';
      const action = single ? `data-sr-rec-add data-variant-id="${live[0].id}"` : 'data-sr-rec-open';
      return `<article class="sr-rec" data-product-id="${p.id}">
        <a class="sr-rec__media" href="${esc(p.url)}" ${single ? '' : 'data-sr-rec-open'} tabindex="-1" aria-hidden="true">${p.image ? `<img src="${esc(p.image)}" alt="" loading="lazy" width="250" height="250">` : ''}${unlocks ? '<span class="sr-rec__flag">Unlocks free shipping</span>' : ''}</a>
        <div class="sr-rec__info">
          <a class="sr-rec__title" href="${esc(p.url)}">${esc(p.title)}</a>
          <p class="sr-rec__price">${compare}<span>${priceLabel}</span></p>
          <button type="button" class="sr-rec__btn" ${action} aria-label="${esc(label + ': ' + p.title)}">${single ? '+ ' : ''}${label}</button>
        </div>
      </article>`;
    }

    /* ---------- one-tap add (single-variant products) ---------- */
    async quickAdd(btn) {
      const card = btn.closest('[data-product-id]');
      btn.disabled = true;
      const txt = btn.textContent;
      btn.textContent = 'Adding…';
      try {
        await this.addVariant(card.dataset.productId, btn.dataset.variantId);
      } catch (e) { btn.disabled = false; btn.textContent = txt; }
    }
    async addVariant(productId, variantId) {
      this.lastAddedProductId = productId;
      await this.cartWrite('/cart/add.js', { items: [{ id: Number(variantId), quantity: 1, properties: { _source: 'SR Cart recommendation' } }] });
      this.announce('Added to cart');
      try { window.Shopify && Shopify.analytics && Shopify.analytics.publish && Shopify.analytics.publish('sr_cart_rec_added', { productId, variantId }); } catch (e) {}
    }

    /* ---------- option picker sheet (multi-variant products) ---------- */
    sheetHost() { return this.panel || this; }
    ensureSheet() {
      if (this._sheet) return this._sheet;
      const host = this.sheetHost();
      const scrim = document.createElement('div');
      scrim.className = 'sr-sheet-scrim';
      scrim.hidden = true;
      scrim.setAttribute('data-sr-sheet-close', '');
      const sheet = document.createElement('div');
      sheet.className = 'sr-sheet';
      sheet.hidden = true;
      sheet.setAttribute('role', 'dialog');
      sheet.setAttribute('aria-modal', 'true');
      sheet.setAttribute('aria-label', 'Choose options');
      sheet.tabIndex = -1;
      host.append(scrim, sheet);
      this._scrim = scrim;
      this._sheet = sheet;
      return sheet;
    }

    openSheet(productId, opener) {
      const rec = this.products.get(String(productId));
      if (!rec) return;
      const p = rec.p;
      const sheet = this.ensureSheet();
      this._sheetOpener = opener;
      // Options with one value (e.g. a single-color listing) are chosen for the shopper and hidden.
      this._sel = p.options.map((o) => (o.values.length === 1 ? o.values[0] : null));
      this._sheetProduct = p;
      sheet.innerHTML = `
        <button type="button" class="sr-sheet__close" data-sr-sheet-close aria-label="Close options">
          <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true"><path d="M4 4l12 12M16 4L4 16" stroke="currentColor" stroke-width="1.6" fill="none" stroke-linecap="round"/></svg>
        </button>
        <div class="sr-sheet__head">
          <div class="sr-sheet__media"><img data-sr-sheet-img src="${esc(p.image)}" alt="" width="220" height="220"></div>
          <div class="sr-sheet__meta">
            <p class="sr-sheet__eyebrow">Made in the USA</p>
            <p class="sr-sheet__title">${esc(p.title)}</p>
            <p class="sr-sheet__price" data-sr-sheet-price></p>
          </div>
        </div>
        ${p.options.map((o, i) => (o.values.length <= 1 ? '' : `
          <fieldset class="sr-opt" data-opt-index="${i}">
            <legend class="sr-opt__label">${esc(o.name)}<span data-sr-opt-chosen></span></legend>
            <div class="sr-opt__chips">
              ${o.values.map((v) => `<button type="button" class="sr-chip" data-sr-chip data-index="${i}" data-value="${esc(v)}" aria-pressed="false">${esc(v)}</button>`).join('')}
            </div>
          </fieldset>`)).join('')}
        <p class="sr-sheet__hint" data-sr-sheet-hint aria-live="polite"></p>
        <button type="button" class="sr-btn sr-btn--gold sr-sheet__add" data-sr-sheet-add>Add to cart</button>`;
      this.syncSheet();
      sheet.hidden = false;
      this._scrim.hidden = false;
      requestAnimationFrame(() => { sheet.classList.add('is-open'); this._scrim.classList.add('is-open'); });
      setTimeout(() => { sheet.classList.add('is-open'); this._scrim.classList.add('is-open'); }, 40);
      const firstChip = sheet.querySelector('.sr-chip:not([disabled])');
      (firstChip || sheet).focus({ preventScroll: true });
    }

    closeSheet() {
      if (!this._sheet || this._sheet.hidden) return;
      this._sheet.classList.remove('is-open');
      this._scrim.classList.remove('is-open');
      const s = this._sheet;
      const sc = this._scrim;
      setTimeout(() => { s.hidden = true; sc.hidden = true; }, 220);
      if (this._sheetOpener && document.contains(this._sheetOpener)) this._sheetOpener.focus({ preventScroll: true });
    }

    matchVariant(sel) {
      const p = this._sheetProduct;
      return p.variants.find((v) => sel.every((s, i) => s == null || v.opts[i] === s));
    }

    syncSheet() {
      const p = this._sheetProduct;
      const sheet = this._sheet;
      const sel = this._sel;
      // Chip states: a value is selectable if some available variant has it alongside the other picks.
      sheet.querySelectorAll('[data-sr-chip]').forEach((chip) => {
        const i = Number(chip.dataset.index);
        const value = chip.dataset.value;
        const trial = sel.slice();
        trial[i] = value;
        const ok = p.variants.some((v) => v.available && trial.every((s, k) => s == null || v.opts[k] === s));
        const on = sel[i] === value;
        chip.classList.toggle('is-on', on);
        chip.setAttribute('aria-pressed', on ? 'true' : 'false');
        chip.classList.toggle('is-out', !ok);
        chip.disabled = !ok && !on;
      });
      sheet.querySelectorAll('.sr-opt').forEach((fs) => {
        const i = Number(fs.dataset.optIndex);
        const span = fs.querySelector('[data-sr-opt-chosen]');
        if (span) span.textContent = sel[i] ? `: ${sel[i]}` : '';
      });
      const complete = sel.every((s) => s != null);
      const exact = complete ? p.variants.find((v) => v.opts.every((o, i) => o === sel[i])) : null;
      const partial = this.matchVariant(sel);
      // Photo follows the shopper's picks (variant image when the product has one).
      const img = sheet.querySelector('[data-sr-sheet-img]');
      const pic = (exact && exact.image) || (partial && partial.image) || p.image;
      if (img && pic && img.getAttribute('src') !== pic) img.src = pic;
      const price = sheet.querySelector('[data-sr-sheet-price]');
      const live = p.variants.filter((v) => v.available);
      const minPrice = Math.min(...live.map((v) => v.price));
      const shown = exact || (live.every((v) => v.price === minPrice) ? live[0] : null);
      if (price) price.innerHTML = shown
        ? `${shown.compare > shown.price ? `<s>${this.money(shown.compare)}</s> ` : ''}${this.money(shown.price)}`
        : `From ${this.money(minPrice)}`;
      const add = sheet.querySelector('[data-sr-sheet-add]');
      const missing = p.options.find((o, i) => sel[i] == null);
      if (exact && exact.available) {
        add.disabled = false;
        add.textContent = `Add to cart · ${this.money(exact.price)}`;
      } else if (exact) {
        add.disabled = true;
        add.textContent = 'Sold out';
      } else {
        add.disabled = false;
        add.textContent = missing ? `Select ${missing.name.toLowerCase()}` : 'Add to cart';
      }
      this._exact = exact;
    }

    pickChip(chip) {
      const i = Number(chip.dataset.index);
      this._sel[i] = this._sel[i] === chip.dataset.value && this._sheetProduct.options[i].values.length > 1 ? null : chip.dataset.value;
      // Drop later picks that no longer combine with this one.
      if (!this.matchVariant(this._sel)) this._sel = this._sel.map((s, k) => (k === i || this._sheetProduct.options[k].values.length === 1 ? s : null));
      const hint = this._sheet.querySelector('[data-sr-sheet-hint]');
      if (hint) hint.textContent = '';
      this.syncSheet();
    }

    async sheetAdd() {
      const p = this._sheetProduct;
      const add = this._sheet.querySelector('[data-sr-sheet-add]');
      if (!this._exact) {
        const idx = p.options.findIndex((o, k) => this._sel[k] == null);
        const fs = this._sheet.querySelector(`.sr-opt[data-opt-index="${idx}"]`);
        const hint = this._sheet.querySelector('[data-sr-sheet-hint]');
        if (hint) hint.textContent = `Please choose a ${p.options[idx].name.toLowerCase()}.`;
        if (fs) { fs.classList.remove('is-shake'); void fs.offsetWidth; fs.classList.add('is-shake'); }
        return;
      }
      add.disabled = true;
      add.textContent = 'Adding…';
      try {
        await this.addVariant(p.id, this._exact.id);
        this.closeSheet();
      } catch (e) {
        add.disabled = false;
        this.syncSheet();
        const hint = this._sheet.querySelector('[data-sr-sheet-hint]');
        if (hint) hint.textContent = e.message || 'Could not add that item.';
      }
    }
  }

  customElements.define('sr-cart', SRCartElement);

  /* Cart page (/cart) recommendations: same engine; after an add, reload so the theme's cart table updates. */
  class SRCartPageRecs extends SRCartElement {
    connectedCallback() {
      this.panel = null;
      this.moneyFormat = this.dataset.moneyFormat;
      this.recCache = new Map();
      this.products = new Map();
      this.lastAddedProductId = null;
      this.bindRecs();
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.closeSheet(); });
      this.renderRecs();
    }
    announce() {}
    loading() {}
    flashError() {}
    async cartWrite(path, body) {
      const data = await postJSON(path, body);
      window.location.reload();
      return data;
    }
    async reconcileGifts() {}
  }
  customElements.define('sr-cart-recs', SRCartPageRecs);

  /* ======================= public API + theme hooks ======================= */
  const get = () => document.querySelector('sr-cart');
  window.SRCart = {
    open: (t) => { const c = get(); if (c) c.open(t); },
    close: () => { const c = get(); if (c) c.close(); },
    refresh: () => { const c = get(); if (c) return c.refresh(); },
    add: (items) => { const c = get(); if (c) return c.cartWrite('/cart/add.js', { items }).then(() => c.open()); },
    formatMoney
  };
  document.addEventListener('sr-cart:refresh', () => window.SRCart.refresh());

  const enabled = () => !!get();
  const onCartPage = () => window.location.pathname.replace(/\/$/, '') === (url('/cart')).replace(/\/$/, '');

  // 1) Blockshop: every route into the theme cart drawer (icon, add-to-cart, quick add) goes through Drawer#open.
  customElements.whenDefined('drawer-element').then(() => {
    const D = customElements.get('drawer-element');
    if (!D || D.prototype.__srPatched) return;
    const orig = D.prototype.open;
    D.prototype.open = function (side, view, trigger) {
      if (view === 'cart-drawer' && enabled()) {
        const c = get();
        if (c.dataset.openOnAdd === 'false') { c.refresh(); return; }
        c.open(trigger);
        return;
      }
      return orig.apply(this, arguments);
    };
    D.prototype.__srPatched = true;
  });

  // 1b) Transition guard: Rebuy loads store-wide via a ScriptTag (not the theme), so until Smart Cart is
  //     switched off in Rebuy it would fight this drawer. Quietly disable Rebuy's cart on this theme only.
  (function quietRebuy(tries) {
    if (!enabled()) return;
    const sc = window.Rebuy && window.Rebuy.SmartCart;
    if (sc && sc.status === 'ready') {
      try { sc.unbindEvents && sc.unbindEvents(); sc.destroy && sc.destroy(); } catch (e) {}
      return;
    }
    if (tries < 40) setTimeout(() => quietRebuy(tries + 1), 500);
  })(0);

  // 2) Remember what was just added so recommendations pair with it.
  window.addEventListener('theme:cart:productAdded', (e) => {
    const c = get();
    const vid = e && e.detail;
    if (!c || !vid) return;
    fetch(url('/cart.js')).then((r) => r.json()).then((cart) => {
      const line = (cart.items || []).find((i) => String(i.variant_id) === String(vid));
      if (line) c.lastAddedProductId = line.product_id;
    }).catch(() => {});
  });

  // 3) Cart icon / any /cart link opens the drawer instead (capture phase, before theme handlers).
  document.addEventListener('click', (e) => {
    if (!enabled() || e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.button > 0) return;
    const a = e.target.closest('[data-drawer-view="cart-drawer"], a[href="/cart"], a[href$="/cart"]');
    if (!a || a.closest('sr-cart') || onCartPage()) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    window.SRCart.open(a);
  }, true);

  // 4) Plain add-to-cart forms the theme doesn't handle (PageFly pages, custom sections).
  document.addEventListener('submit', (e) => {
    const form = e.target;
    if (!enabled() || e.defaultPrevented || !(form instanceof HTMLFormElement)) return;
    const action = form.getAttribute('action') || '';
    if (!/\/cart\/add(\.js)?$/.test(action.split('?')[0])) return;
    if (form.matches('.product-form, [data-sr-skip]') || form.closest('product-form, form-element, quick-add, product-quick-add, sr-cart, [data-sr-skip]')) return; // theme handles its own forms (Blockshop: form.product-form)
    e.preventDefault();
    const fd = new FormData(form);
    const items = [{ id: Number(fd.get('id')), quantity: Number(fd.get('quantity') || 1), properties: {} }];
    for (const [k, v] of fd.entries()) { const m = k.match(/^properties\[(.+)\]$/); if (m && v !== '') items[0].properties[m[1]] = v; }
    const sp = fd.get('selling_plan'); if (sp) items[0].selling_plan = Number(sp);
    window.SRCart.add(items).catch((err) => { console.warn('[SR Cart] add failed', err); form.submit(); });
  }, true);
})();